<template>
    <div class="seller-profile">
        <Toast />

        <div class="seller-profile__hero">
            <div class="seller-profile__hero-bg"></div>

            <div class="seller-profile__container">

                <div class="seller-profile__header">
                    <div class="seller-profile__avatar-container">
                        <img :src="seller?.profile_pic || '/favicon.ico'" alt="Profile avatar" class="seller-profile__avatar">

                    </div>

                    <div class="seller-profile__info">
                        <div class="seller-profile__name-row">
                            <h1 class="seller-profile__name">{{ seller?.username || 'No name available' }}</h1>

                        </div>

                        <div class="seller-profile__wallet">
                            <div class="seller-profile__wallet-address-container">
                                <span class="seller-profile__wallet-label">Wallet Address:</span>
                                <span class="seller-profile__wallet-address">{{ formattedWalletAddress }}</span>
                                <button class="seller-profile__copy-btn" @click="copyWallet">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                        viewBox="0 0 24 24">
                                        <path stroke="currentColor" stroke-linejoin="round" stroke-width="2"
                                            d="M9 8v3a1 1 0 0 1-1 1H5m11 4h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v1m4 3v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7.13a1 1 0 0 1 .24-.65L7.7 8.35A1 1 0 0 1 8.46 8H13a1 1 0 0 1 1 1Z" />
                                    </svg>
                                    <span v-if="showCopied" class="seller-profile__copied-text">
                                        Copied!
                                    </span>
                                    <span v-else class="seller-profile__hover-text">
                                        Click to copy
                                    </span>
                                </button>
                            </div>
                        </div>

                        <p class="seller-profile__bio">{{ seller?.bio || 'No bio available' }}</p>


                    </div>
                </div>

                <Tabs v-model:activeTab="activeTab" :tabs="tabs" />

            </div>
        </div>


        <div class="seller-profile__container">
            <div class="seller-profile__subtabs-container">
                <SubTabs v-model:activeSubTab="activeSubTab" :subtabs="subTabs" />
            </div>
            <div class="seller-profile__grid">
                <div v-if="isLoading[activeSubTab]" class="col-span-full flex justify-center items-center py-12">
                    <div class="loading-spinner"></div>
                </div>
                
                <template v-else>
                    <Card :is-add-card="true" @add="handleAddNew" />
                    <Card v-for="item in visibleItems" 
                          :key="item.id" 
                          :title="item.title || item.name"
                          :description="item.description" 
                          :file_size="item.file_size" 
                          :eval_price="item?.eval_price || null"
                          :image="item.image"
                          :tags="item.tags || []" 
                          :delivery_method="item.delivery_method" 
                          :price="item.price"
                          :currency="item.currency" 
                          :status="item.status" 
                          :show-edit="activeSubTab !== 'datasets'"
                          @edit="handleEditItem(item)" 
                    />
                </template>
            </div>
        </div>
    </div>
    <Modal :show="showModal" :title="editingItem ? 'Edit Item' : 'Add New Item'" :initial-data="editingItem"
        :fields="cardFormFields" :available-tags="tags" @close="showModal = false" @save="handleSaveItem">
        <template #field="{ field, modelValue, updateModelValue }">
            <template v-if="field.type === 'select'">
                <SelectInput
                    :id="field.key"
                    :model-value="modelValue"
                    @update:model-value="(value) => {
                        updateModelValue(value);
                    }"
                    :options="field.options"
                    :placeholder="field.placeholder"
                    :required="field.required"
                />
            </template>
            <template v-else-if="field.type === 'image-upload'">
                <div class="image-upload-container">
                    <input 
                        type="file" 
                        :id="field.key" 
                        accept="image/*"
                        @change="(e) => handleImageUpload(e.target.files[0], updateModelValue)"
                        class="image-upload-input"
                    />
                    <label :for="field.key" class="image-upload-label">
                        <span v-if="!modelValue">Choose an image</span>
                        <span v-else>Image selected</span>
                    </label>
                    <div v-if="modelValue" class="image-preview">
                        <img :src="modelValue" alt="Preview" class="preview-img" />
                    </div>
                    <p v-if="field.hint" class="field-hint">{{ field.hint }}</p>
                </div>
            </template>
        </template>
    </Modal>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import axios from '@/plugins/axios';
  import { fetchDatasets, fetchListings } from '@/services/api';
  import { useLayoutComponents } from '@/composables/layout.js';
  import Tabs from '@/components/Tabs.vue';
  import Modal from '@/components/modals/BaseModal.vue';
  import { cacheService } from '@/services/cache-service';

  const { SubTabs, Card } = useLayoutComponents();
  const route = useRoute();
  const router = useRouter();

  const sellerId = route.params.id;

  const seller = ref(null);
  const datasets = ref([]);
  const listings = ref([]);
  const isLoading = ref(true);
  const currentPage = ref(1);
  const itemsPerPage = ref(8);

  const activeSubTab = ref('datasets');
  const subTabs = [
    { id: 'datasets', label: 'Datasets' },
    { id: 'listings', label: 'Listings' }
  ];

  const fetchSellerProfile = async () => {
    try {
      const sellerResponse = await axios.get(`/api/sellers/${sellerId}`);
      seller.value = sellerResponse.data;
      seller.value.profile_pic = cacheService.bufferToBase64(seller.value.profile_pic);

      const [allDatasets, allListings] = await Promise.all([
        fetchDatasets(),
        fetchListings()
      ]);

      datasets.value = allDatasets.filter(ds => ds.owner_user_id == seller.value.id);
      listings.value = allListings.filter(ls => ls.owner_user_id == seller.value.id);
    } catch (error) {
      console.error('Error fetching seller profile:', error);
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => {
    fetchSellerProfile().then(() => {
      if (seller.value && seller.value.username && route.params.id !== seller.value.username) {
        router.replace(`/seller-profile/${seller.value.username}`);
      } else {
        router.replace('/explore');
      }
    });
  });

  const currentItems = computed(() => {
    return activeSubTab.value === 'datasets' ? datasets.value : listings.value;
  });

  const visibleItems = computed(() => {
    return currentItems.value.slice(0, currentPage.value * itemsPerPage.value);
  });
</script>

<style scoped lang="postcss">
  .seller-profile {
      @apply min-h-screen bg-gray-200 dark:bg-slate-900;
  }

  .seller-profile__hero {
      @apply relative pt-16 pb-6 mb-6 border-b border-gray-200 dark:border-white/10 bg-gray-100;
      background-size: 100% 100%;
      background-position: 0px 0px;
      background-repeat: no-repeat;
  }

  .dark .seller-profile__hero {
      background: linear-gradient(to bottom, #1e293b, #0f172a, #020617);
  }

  .seller-profile__hero-bg {
      @apply absolute inset-0 bg-cover bg-center bg-gray-100;
      opacity: 0.1;
  }

  .dark .seller-profile__hero-bg {
      opacity: 0.1;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
  }

  .seller-profile__container {
      @apply relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8;
  }

  .seller-profile__header {
      @apply flex flex-col md:flex-row gap-8 items-center py-10;
  }

  .seller-profile__avatar-container {
      @apply relative flex-shrink-0 text-center;
  }

  .profile-photo {
      @apply relative mx-auto w-32 h-32;
  }

  .profile-photo__img {
      @apply w-full h-full rounded-full object-cover ring-4 ring-indigo-500/30 dark:ring-purple-500/50;
  }

  .profile-photo__upload-btn {
      @apply absolute bottom-2 right-2 p-2 rounded-full;
      @apply bg-white/90 backdrop-blur-sm text-gray-700 shadow-lg;
      @apply dark:bg-white/20 dark:text-white;
      @apply hover:bg-white dark:hover:bg-white/30;
      @apply transition-colors duration-200;
  }

  .profile-photo__help {
      @apply mt-4 text-sm text-gray-600 dark:text-gray-400 max-w-[200px];
  }

  .seller-profile__info {
      @apply flex-1;
  }

  .seller-profile__name-row {
      @apply flex flex-col xs:flex-row items-center xs:justify-between gap-4;
  }

  .seller-profile__grid {
      @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 pb-8 pt-2;
      min-height: 200px;
      position: relative;
  }

  .seller-profile__title {
      @apply text-3xl font-bold text-center xs:text-left text-gray-800 dark:text-white tracking-tight;
  }


  .seller-profile__subtitle {
      @apply mt-2 text-gray-600 dark:text-gray-300;
  }

  .settings-form {
      @apply max-w-5xl mx-auto space-y-6 pb-8;
  }

  .settings-form__grid {
      @apply grid grid-cols-1 lg:grid-cols-2 gap-6;
  }

  .settings-card {
      @apply bg-gray-50 dark:bg-white/5 rounded-2xl overflow-hidden;
      @apply border border-gray-200 dark:border-white/10;
  }

  .settings-card__header {
      @apply px-6 py-4 bg-gray-100/50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10;
  }

  .settings-card__title {
      @apply text-lg font-medium text-gray-900 dark:text-white;
  }

  .settings-card__description {
      @apply mt-1 text-sm text-gray-600 dark:text-gray-400;
  }

  .settings-card__content {
      @apply p-6 space-y-4;
  }

  .form-group {
      @apply space-y-2;
  }

  .form-group label {
      @apply block text-sm font-medium text-gray-700 dark:text-gray-300;
  }

  .form-group input {
      @apply w-full px-4 py-2 rounded-lg border transition-colors;
      @apply bg-white dark:bg-white/5;
      @apply border-gray-200 dark:border-white/10;
      @apply text-gray-900 dark:text-white;
      @apply focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50;
      @apply dark:focus:ring-purple-500/20 dark:focus:border-purple-500/50;
  }

  .form-actions {
      @apply flex items-center justify-end gap-4 mt-8;
  }

  .save-button {
      @apply px-6 py-2.5 rounded-lg text-sm font-medium transition-colors;
      @apply bg-purple-600 text-white;
      @apply hover:bg-purple-700;
      @apply dark:bg-purple-500 dark:hover:bg-purple-600;
  }

  .cancel-button {
      @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors dark:hover:border-indigo-500/50 dark:hover:text-indigo-400 dark:bg-white/10 dark:text-white dark:border-white/20 hover:border-indigo-300 hover:text-indigo-600 bg-white text-gray-700 border border-gray-200;
  }

  .form-section {
      @apply bg-gray-50 dark:bg-white/5 rounded-2xl overflow-hidden;
      @apply border border-gray-200 dark:border-white/10;
  }
</style>
